import React, { useState, useMemo, useReducer } from 'react';
import { Buffer } from 'buffer';
import { gql, useMutation, useQuery } from '@apollo/client';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Dropzone, { useDropzone } from 'react-dropzone';
import { Grid, AccordionSummary, AccordionDetails, Button, Accordion, Box, Typography, TextField, Autocomplete } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import SwitchButton from '../common/formControls/SwitchButton';
import Status from './Status';
import Coop from './Coop';
import { IUser } from '../../types/user';
import { unitStatusTitles } from '../../utils/Constants';
import { IStatus, IExecutor, ICoopRate, ICoopStructure, IProject, IAddress, IHoursOfOperation } from '../../types/project';
import TextInput from '../common/formControls/TextInput';
import PdfCard from '../common/PdfCard';
import { statusReducer, executorReducer, coopReducer } from '../../features/project/projectHooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import { useAppDispatch } from '../../app/hooks';
import { selectProject, setProject } from '../../features/project/projectSlice';
import { selectUser } from '../../features/auth/authSlice';
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from '../../utils/Constants';
import { FlexEnd } from '../../commonStyles';

// Full day names - the API's appointment schedule matches hoursOfOperation.day against these
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const GeneralInfo = (props: ChildProps) => {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const project = useSelector(selectProject);
  const [users, setUsers] = useState<IUser[]>([]);

  const [name, setName] = useState(project.name);
  const [developerName, setDeveloperName] = useState(project.developerName);
  const [addresses, setAddresses] = useState(project.addresses);
  // Hours of operation, one row per day. Stored as seconds since midnight; a day without both times isn't saved and
  // falls back to the default public appointment hours.
  const [hoursOfOperation, setHoursOfOperation] = useState<IHoursOfOperation[] | undefined>(project.hoursOfOperation);
  const [email, setEmail] = useState(project.email);
  const [docusignEmail, setDocusignEmail] = useState(project.docusignEmail);
  const [trackingPhone, setTrackingPhone] = useState(project.trackingPhone);
  const [salesTrackingPhone, setSalesTrackingPhone] = useState(project.salesTrackingPhone);
  const [logoImage, setLogoImage] = useState<any>(project.logoGetUrl);
  const [buildingImage, setBuildingImage] = useState<any>(project.imageGetUrl);
  const [developerImage, setDeveloperImage] = useState<any>(project.developerLogoGetUrl);
  const [partnerImage, setPartnerImage] = useState<any>(project.partnerLogoGetUrl);
  const [logo, setLogo] = useState<any>(null);
  const [building, setBuilding] = useState<any>(null);
  const [developer, setDeveloper] = useState<any>(null);
  const [partner, setPartner] = useState<any>(null);
  const [commissionIncludeOptions, setCommissionIncludeOptions] = useState<boolean>(project.commissionIncludeOptions!);
  const [statusState, statusDispatch] = useReducer(statusReducer, project.status);
  const [executorState, executorDispatch] = useReducer(executorReducer, project.executors);
  const [coopState, coopDispatch] = useReducer(coopReducer, project.coopStructures);
  const [mortgageLimit, setMortgageLimit] = useState<number>(project?.mortgageLimit!);
  const [mortgageMinDay, setMortgageMinDay] = useState<number>(project?.mortgageMinDay!);
  const [hideOccupancy, setHideOccupancy] = useState<boolean>(project.hideOccupancy);
  const [leasing, setLeasing] = useState<boolean>(project.leasing);
  const [leaseCc, setLeaseCc] = useState<IUser[]>(project.leaseCc);
  const [defaultZeroValue, setDefaultZeroValue] = useState<string>(project.defaultZeroValue);
  const [lawyer, setLawyer] = useState<string>(project.lawyer);
  const [tagLine, setTagLine] = useState<string>(project.tagLine);
  const [combinedProjects, setCombinedProjects] = useState<string[]>(
    project.combinedProjects.length ? project.combinedProjects.map((combinedProject: IProject) => combinedProject.name) : []
  );
  const [firstTentativeOccupancy, setFirstTentativeOccupancy] = useState<Date | null>(project.firstTentativeOccupancy);
  const [finalTentativeOccupancy, setFinalTentativeOccupancy] = useState<Date | null>(project.finalTentativeOccupancy);
  const [firmOccupancy, setFirmOccupancy] = useState<Date | null>(project.firmOccupancy);
  const [outsideOccupancy, setOutsideOccupancy] = useState<Date | null>(project.outsideOccupancy);
  const [salesOffice, setSalesOffice] = useState<string>(project.salesOffice);
  const [active, setActive] = useState<boolean>(project.active);
  const [acknowledgement, setAcknowledgement] = useState<boolean>(project.acknowledgement);
  const [executionDeposit, setExecutionDeposit] = useState<boolean>(project.executionDeposit);
  const [automatedReminders, setAutomatedReminders] = useState<boolean>(project.automatedReminders);
  const [postRegistration, setPostRegistration] = useState<boolean>(project.postRegistration);
  const [netHst, setNetHst] = useState<boolean>(project.netHst);
  const [tagValue, setTagValue] = useState<string>('');
  const [tags, setTags] = useState<string[]>(project.tags ? project.tags : []);
  const { title, type } = props;

  const projects = user.projectAccess.map((project: any) => {
    return project.project;
  });

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/jpeg, image/jpg, image/png',
  });

  // Mutation

  const [updateProject] = useMutation(UPDATEPROJECT, {
    onCompleted: (data) => {
      storeDispatch(setProject(data.projectUpdateById.record));
      storeDispatch(showSuccessSnackbar('Project Information has been updated!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateUserProject] = useMutation(UPDATEUSER, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Project has been created'));
      navigate('/portal');
    },
    onError: (err: any) => {
      console.log(err, 'err');
    },
  });

  const [saveProject] = useMutation(CREATEPROJECT, {
    onCompleted: (data) => {
      let projectIds: any = user?.projectAccess.map((project: any) => {
        return {
          ...project,
          project: project.project._id,
        };
      });
      updateUserProject({
        variables: { _id: user?._id, record: { projectAccess: [...projectIds, { project: data.projectCreateOne._id, access: [] }] } },
      });
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useQuery(GETUSERS, {
    variables: {
      filter: { OR: [{ type: 'Sales' }, { type: 'Manager' }, { type: 'Admin' }, { type: 'Developer' }], locked: false },
    },
    onCompleted: (data) => {
      let sortedUser = [...data.userMany].sort((a, b) => a.fullName.localeCompare(b.fullName));
      setUsers(sortedUser);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const handleSubmit = (event: any) => {
    // Save Project object only if all the mandatory fields are filled
    event.preventDefault();
    if (!addresses.length) return storeDispatch(showErrorSnackbar('No Project Address Found'));
    const invalidHours = (hoursOfOperation || []).find((hours) => hours.close <= hours.open);
    if (invalidHours) return storeDispatch(showErrorSnackbar(`${invalidHours.day}: the closing time must be after the opening time`));
    let phone = '';
    let salesPhone = '';
    if (trackingPhone) {
      phone = trackingPhone.toString().replace(/\D/g, '');
    }
    if (salesTrackingPhone) {
      salesPhone = salesTrackingPhone.toString().replace(/\D/g, '');
    }

    let combinedProjectIds = [];

    if (combinedProjects.length) {
      combinedProjectIds = combinedProjects.map((project: string) => {
        let selectedProject = projects.find((allProject: IProject) => allProject.name === project);
        if (selectedProject) {
          return selectedProject._id;
        } else return null;
      });
    }

    if (type === 'create') {
      saveProject({
        variables: {
          record: {
            name,
            developerName,
            addresses,
            email,
            docusignEmail,
            trackingPhone: parseInt(phone!, 10),
            salesTrackingPhone: parseInt(salesPhone!, 10),
            coopStructures: coopState,
            depositStructures: [],
            options: [],
            emailTemplates: [],
            combinedProjects: combinedProjectIds,
            firstTentativeOccupancy,
            finalTentativeOccupancy,
            firmOccupancy,
            outsideOccupancy,
            mergeTemplates: [
              {
                name: 'floorplan',
                apsTemplates: []
              },
              {
                name: 'warranty information sheet',
                apsTemplates: []
              }
            ],
            active,
            acknowledgement,
            executionDeposit,
            automatedReminders,
            postRegistration,
            netHst,
            leasing,
            leaseCc: leaseCc.map((user: IUser) => user._id),
            tags,
          },
        },
      });
    } else {
      // const { _id, emailTemplates, logoGetUrl, logoPutUrl, imageGetUrl, imagePutUrl, portal, appointmentSettings, ...newObject } = project;
      updateProject({
        variables: {
          _id: project?._id,
          record: {
            name,
            developerName,
            addresses,
            // Only sent once loaded, so a project loaded without it can't wipe the saved hours
            ...(hoursOfOperation !== undefined ? { hoursOfOperation } : {}),
            email,
            docusignEmail,
            trackingPhone: parseInt(phone!, 10),
            salesTrackingPhone: parseInt(salesPhone!, 10),
            executors: executorState,
            coopStructures: coopState,
            commissionIncludeOptions,
            status: statusState,
            mortgageLimit,
            mortgageMinDay,
            hideOccupancy,
            defaultZeroValue,
            lawyer,
            tagLine,
            salesOffice,
            combinedProjects: combinedProjectIds,
            firstTentativeOccupancy,
            finalTentativeOccupancy,
            firmOccupancy,
            outsideOccupancy,
            active,
            acknowledgement,
            executionDeposit,
            automatedReminders,
            postRegistration,
            netHst,
            leasing,
            leaseCc: leaseCc.map((user: IUser) => user._id),
            tags,
          },
        },
      });
    }
  };

  const handleDrop = (acceptedFiles: any, type: string) => {
    if (acceptedFiles.length === 0) {
      storeDispatch(showErrorSnackbar('This file type is not allowed'));
      return;
    }
    const file = acceptedFiles[0];
    const fileReader = new FileReader();
    if (file) {
      fileReader.readAsDataURL(file);
    }
    fileReader.onloadend = async () => {
      if (type === 'logo') {
        setLogo(acceptedFiles[0]);
        setLogoImage(fileReader.result);
      } else if (type === 'building') {
        setBuilding(acceptedFiles[0]);
        setBuildingImage(fileReader.result);
      } else if (type === 'developer') {
        setDeveloper(acceptedFiles[0]);
        setDeveloperImage(fileReader.result);
      } else if (type === 'partner') {
        setPartner(acceptedFiles[0]);
        setPartnerImage(fileReader.result);
      }
    };
  };

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  // Statuses and Color
  const addStatus = () => {
    statusDispatch({ type: 'ADD' });
  };

  const handleStatusInput = (event: any, numberIndex: number) => {
    statusDispatch({ type: 'UPDATE', payload: { index: numberIndex, field: event.target.name, value: event.target.value } });
  };

  const handleColorInput = (numberIndex: number, color: any) => {
    statusDispatch({ type: 'UPDATE', payload: { index: numberIndex, field: 'color', value: color } });
  };

  const handleDeleteStatus = (numberIndex: number) => {
    statusDispatch({ type: 'DELETE', payload: { index: numberIndex } });
  };

  // Executor

  const addExecutor = () => {
    executorDispatch({ type: 'ADD' });
  };

  const handleExecutorInput = (event: any, numberIndex: number) => {
    executorDispatch({ type: 'UPDATE', payload: { index: numberIndex, field: event.target.name, value: event.target.value } });
  };

  const handleExecutorCc = (cc: boolean, numberIndex: number) => {
    executorDispatch({ type: 'UPDATE', payload: { index: numberIndex, field: 'cc', value: cc } });
  };

  const handleExecutorCcDocuments = (ccAll: boolean, numberIndex: number) => {
    executorDispatch({ type: 'UPDATE', payload: { index: numberIndex, field: 'ccAll', value: ccAll } });
  };

  const handleExecutorCcNotice = (ccNotice: boolean, numberIndex: number) => {
    executorDispatch({ type: 'UPDATE', payload: { index: numberIndex, field: 'ccNotice', value: ccNotice } });
  };

  const handleDeleteExecutor = (numberIndex: number) => {
    executorDispatch({ type: 'DELETE', payload: { index: numberIndex } });
  };

  // Commission

  const addCoopStructure = () => {
    coopDispatch({ type: 'ADDCOOPSTRUCTURE' });
  };

  const addCoopRate = (index: number) => {
    coopDispatch({ type: 'ADDCOOPRATE', payload: { index: index } });
  };

  const deleteCoopStructure = (index: number) => {
    coopDispatch({ type: 'DELETECOOPSTRUCTURE', payload: { index: index } });
  };

  const handleDeleteCoopRate = (numberIndex: number, structureIndex: number) => {
    coopDispatch({ type: 'DELETECOOPRATE', payload: { index: numberIndex, structureIndex: structureIndex } });
  };

  const handleCoopRateInput = (event: any, numberIndex: number, type: string, structureIndex: number) => {
    coopDispatch({
      type: 'UPDATE',
      payload: {
        index: numberIndex,
        structureIndex: structureIndex,
        field: event.target.name,
        value:
          event.target.name === 'days' || event.target.name === 'percentage' || event.target.name === 'fixedAmount'
            ? parseFloat(event.target.value)
            : event.target.value,
      },
    });
  };

  const handleCoopDate = (newValue: any, numberIndex: number, structureIndex: number) => {
    coopDispatch({
      type: 'UPDATE',
      payload: {
        structureIndex: structureIndex,
        index: numberIndex,
        field: 'date',
        value: newValue,
      },
    });
  };
  // Images

  const saveImages = async () => {
    if (logo || building || developer || partner) {
      if (logo) {
        const buffer = Buffer.from(logoImage.replace(/^data:image\/\w+;base64,/, ''), 'base64');
        const options = {
          headers: { 'Content-Type': logo.type },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };
        await axios.put(project.logoPutUrl!, buffer, options);
      }
      if (building) {
        const buffer = Buffer.from(buildingImage.replace(/^data:image\/\w+;base64,/, ''), 'base64');
        const options = {
          headers: { 'Content-Type': building.type },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };
        await axios.put(project.imagePutUrl!, buffer, options);
      }
      if (developer) {
        const buffer = Buffer.from(developerImage.replace(/^data:image\/\w+;base64,/, ''), 'base64');
        const options = {
          headers: { 'Content-Type': developer.type },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };
        await axios.put(project.developerLogoPutUrl!, buffer, options);
      }
      if (partner) {
        const buffer = Buffer.from(partnerImage.replace(/^data:image\/\w+;base64,/, ''), 'base64');
        const options = {
          headers: { 'Content-Type': partner.type },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };
        await axios.put(project.partnerLogoPutUrl!, buffer, options);
      }
      await storeDispatch(showSuccessSnackbar('Image Updated'));
    } else {
      storeDispatch(showErrorSnackbar('No Image Found'));
    }
  };

  const addAddress = () => {
    setAddresses([
      ...addresses,
      {
        streetAddress: '',
        city: '',
        province: '',
        country: '',
        postalCode: '',
      },
    ]);
  };

  const handleAddress = (e: any, numIndex: number) => {
    let address = addresses.map((address: IAddress, index: number) => {
      if (index === numIndex) {
        return {
          ...address,
          [e.target.name]: e.target.value,
        };
      } else return address;
    });
    setAddresses(address);
  };

  // Hours of operation: seconds since midnight <-> the "HH:MM" a time input uses
  const secondsToTime = (seconds: number) =>
    `${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}`;
  const timeToSeconds = (time: string) => {
    const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10));
    return hours * 3600 + minutes * 60;
  };

  const handleHours = (day: string, field: 'open' | 'close', time: string) => {
    const current = hoursOfOperation || [];
    const existing = current.find((hours: IHoursOfOperation) => hours.day === day);
    if (!time) {
      // Clearing either time removes the day (it then uses the default hours)
      setHoursOfOperation(current.filter((hours: IHoursOfOperation) => hours.day !== day));
      return;
    }
    const seconds = timeToSeconds(time);
    if (existing) {
      setHoursOfOperation(current.map((hours: IHoursOfOperation) => (hours.day === day ? { ...hours, [field]: seconds } : hours)));
    } else {
      // A new day starts with a one-hour window around the time entered, so it's valid straight away
      const added = field === 'open' ? { day, open: seconds, close: Math.min(seconds + 3600, 86340) } : { day, open: Math.max(seconds - 3600, 0), close: seconds };
      setHoursOfOperation(DAYS_OF_WEEK.map((d) => (d === day ? added : current.find((h) => h.day === d))).filter(Boolean) as IHoursOfOperation[]);
    }
  };

  const clearHours = (day: string) => {
    setHoursOfOperation((hoursOfOperation || []).filter((hours: IHoursOfOperation) => hours.day !== day));
  };

  const deleteAddress = (numIndex: number) => {
    setAddresses(addresses.filter((address: IAddress, index: number) => numIndex !== index));
  };

  const addTag = () => {
    if (tagValue) {
      setTagValue('');
      setTags([...tags, tagValue]);
    } else return storeDispatch(showErrorSnackbar('No Tag Value Found'));
  };

  const deleteTag = (numIndex: number) => {
    let filteredTags = tags.filter((tag: string, index: number) => numIndex !== index);
    setTags(filteredTags);
  };

  return (
    <form id="project" onSubmit={handleSubmit} autoComplete="off">
      <h2>{title}</h2>
      <Box
        sx={{
          border: '1px solid #000',
          p: 3,
          position: 'relative',
          borderRadius: '8px',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Project Name'}
              title={'Project Name'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              value={name}
              required={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Developer Name'}
              title={'Developer Name'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeveloperName(e.target.value)}
              value={developerName}
              required={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Email'}
              title={'Email'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              value={email}
              required={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Docusign Email'}
              title={'Docusign Email'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocusignEmail(e.target.value)}
              value={docusignEmail}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Online OSC Number'}
              title={'Online OSC Number'}
              type="number"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrackingPhone(e.target.value)}
              value={trackingPhone}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Onsite Sales Office Number'}
              title={'Onsite Sales Office Number'}
              type="number"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSalesTrackingPhone(e.target.value)}
              value={salesTrackingPhone}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Sales Office'}
              title={'Sales Office'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSalesOffice(e.target.value)}
              value={salesOffice}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <TextField
              fullWidth
              label={'Lawyer'}
              title={'Lawyer'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLawyer(e.target.value)}
              value={lawyer}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Autocomplete
              sx={{ height: '100%' }}
              multiple
              options={projects.map((project: IProject) => project.name)}
              getOptionLabel={(option: string) => option}
              isOptionEqualToValue={(option, value) => option === value}
              disableClearable={false}
              freeSolo={false}
              value={combinedProjects}
              onChange={(event: any, newValue: any | null) => {
                setCombinedProjects(newValue.map((option: string) => option));
              }}
              renderInput={(params) => <TextField {...params} label="Combined Projects" size="medium" />}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={'Tag Line'}
              title={'TagLine'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagLine(e.target.value)}
              value={tagLine}
            />
          </Grid>
          <Grid item xs={3}>
            <SwitchButton state={active} handleChange={() => setActive(!active)} title={'Active'} />
          </Grid>
          <Grid item xs={3}>
            <SwitchButton
              state={acknowledgement}
              handleChange={() => setAcknowledgement(!acknowledgement)}
              title={'Acknowledgement Step'}
            />
          </Grid>
          <Grid item xs={3}>
            <SwitchButton
              state={executionDeposit}
              handleChange={() => setExecutionDeposit(!executionDeposit)}
              title={'Deposit Dates Reflect Time of Execution'}
            />
          </Grid>
          <Grid item xs={3}>
            <SwitchButton
              state={automatedReminders}
              handleChange={() => setAutomatedReminders(!automatedReminders)}
              title={'Automated Reminders for Outstandings'}
            />
          </Grid>
          <Grid item xs={3}>
            <SwitchButton
              state={postRegistration}
              handleChange={() => setPostRegistration(!postRegistration)}
              title={'Post Registration'}
            />
          </Grid>
          <Grid item xs={3}>
            <SwitchButton
              state={netHst}
              handleChange={() => setNetHst(!netHst)}
              title={'Net Hst Pricing'}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <h4>Logo</h4>
            {logoImage ? (
              <PdfCard file={logoImage} title={'Logo'} id={'0'} handleDelete={() => setLogoImage(null)} download={true} />
            ) : (
              <Dropzone onDrop={(files) => handleDrop(files, 'logo')} accept="image/jpg, image/jpeg, image/png">
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps({ style })}>
                      <input {...getInputProps()} />
                      <p>Drag and Drop or Upload the Project's Logo (.jpg)</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            )}
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <h4>Building Image</h4>
            {buildingImage ? (
              <PdfCard file={buildingImage} title={'Building'} id={'0'} handleDelete={() => setBuildingImage(null)} download={true} />
            ) : (
              <Dropzone onDrop={(files) => handleDrop(files, 'building')} accept="image/jpg, image/jpeg, image/png">
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps({ style })}>
                      <input {...getInputProps()} />
                      <p>Drag and Drop or Upload the Building's Image</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            )}
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <h4>Developer Logo</h4>
            {developerImage ? (
              <PdfCard
                file={developerImage}
                title={'Developer Logo'}
                id={'0'}
                handleDelete={() => setDeveloperImage(null)}
                download={true}
              />
            ) : (
              <Dropzone onDrop={(files) => handleDrop(files, 'developer')} accept="image/jpg, image/jpeg, image/png">
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps({ style })}>
                      <input {...getInputProps()} />
                      <p>Drag and Drop or Upload the Developer's Logo (.jpg)</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            )}
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <h4>Partner Logo</h4>
            {partnerImage ? (
              <PdfCard file={partnerImage} title={'Partner Logo'} id={'0'} handleDelete={() => setPartnerImage(null)} download={true} />
            ) : (
              <Dropzone onDrop={(files) => handleDrop(files, 'partner')} accept="image/jpg, image/jpeg, image/png">
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps({ style })}>
                      <input {...getInputProps()} />
                      <p>Drag and Drop or Upload the Partner's Logo (.jpg)</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            )}
          </Grid>
        </Grid>
        <div>
          <Button color="primary" variant="contained" onClick={() => saveImages()}>
            Save Images
          </Button>
        </div>
      </Box>
      <h2>Addresses</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        {addresses.length ? (
          <Grid container spacing={2}>
            {addresses.map((address: IAddress, index: number) => {
              return (
                <React.Fragment key={index}>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label={'Address'}
                      title={'Address'}
                      name="streetAddress"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAddress(e, index)}
                      value={address.streetAddress}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label={'City'}
                      title={'City'}
                      name="city"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAddress(e, index)}
                      value={address.city}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label={'Province'}
                      title={'Province'}
                      name="province"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAddress(e, index)}
                      value={address.province}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label={'Country'}
                      title={'Country'}
                      name="country"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAddress(e, index)}
                      value={address.country}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label={'Postal Code'}
                      title={'Postal Code'}
                      name="postalCode"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAddress(e, index)}
                      value={address.postalCode}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button variant="contained" onClick={() => deleteAddress(index)} color="error">
                      Delete
                    </Button>
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        ) : (
          <Box>
            <Box>There are currently no addresses set up.</Box>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={() => addAddress()}>
            Add an Address
          </Button>
        </Box>
      </Box>
      <h2>Hours of Operation</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <Typography sx={{ mb: 2, fontSize: '0.9rem' }}>
          Used for the public appointment schedule. A day left blank uses the default hours (12:00 PM to 6:00 PM, 5:00 PM on
          weekends).
        </Typography>
        <Grid container spacing={2} sx={{ maxWidth: '760px' }}>
          {DAYS_OF_WEEK.map((day: string) => {
            const hours = (hoursOfOperation || []).find((h: IHoursOfOperation) => h.day === day);
            return (
              <React.Fragment key={day}>
                <Grid item xs={12} sm={2} sx={{ alignSelf: 'center' }}>
                  <strong>{day}</strong>
                </Grid>
                <Grid item xs={5} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    label="Open"
                    InputLabelProps={{ shrink: true }}
                    value={hours ? secondsToTime(hours.open) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleHours(day, 'open', e.target.value)}
                  />
                </Grid>
                <Grid item xs={5} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    label="Close"
                    InputLabelProps={{ shrink: true }}
                    value={hours ? secondsToTime(hours.close) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleHours(day, 'close', e.target.value)}
                    error={!!hours && hours.close <= hours.open}
                    helperText={hours && hours.close <= hours.open ? 'Must be after open' : ''}
                  />
                </Grid>
                <Grid item xs={2} sm={2} sx={{ alignSelf: 'center' }}>
                  {hours ? (
                    <Button size="small" color="error" onClick={() => clearHours(day)}>
                      Clear
                    </Button>
                  ) : null}
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      </Box>
      <h2>Custom Status</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography>Default Status</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {unitStatusTitles.map((defaultStatus: IStatus, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <Status
                      type={'default'}
                      key={index}
                      numberIndex={index}
                      value={defaultStatus.name}
                      title={defaultStatus.name}
                      code={defaultStatus.code}
                      color={defaultStatus.color}
                      statusType={defaultStatus.type}
                      category={defaultStatus.category}
                      disabled={true}
                    />
                  </React.Fragment>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Grid container spacing={2}>
          {statusState.map((status: IStatus, index: number) => {
            return (
              <React.Fragment key={index}>
                <Status
                  type={'custom'}
                  numberIndex={index}
                  key={index}
                  value={status.name}
                  title={status.name}
                  code={status.code}
                  color={status.color}
                  statusType={status.type}
                  category={status.category}
                  disabled={false}
                  handleStatusInput={handleStatusInput}
                  handleDelete={handleDeleteStatus}
                  handleColorInput={handleColorInput}
                  statusDispatch={statusDispatch}
                />
              </React.Fragment>
            );
          })}
          <Grid sx={{ mt: 1 }} item xs={12} sm={4}>
            <Button variant="contained" color="primary" onClick={() => addStatus()}>
              Insert Custom Status
            </Button>
          </Grid>
        </Grid>
      </Box>
      <h2>Executors</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        {executorState.length > 0 ? (
          <Grid container spacing={2}>
            {executorState.map((executor: IExecutor, index: number) => {
              return (
                <React.Fragment key={index}>
                  <Grid item xs={12} sm={3} md={3} lg={3} xl={3}>
                    <TextInput
                      name={'name'}
                      title={'Executor Name'}
                      label={'name'}
                      handleTextInput={(e: any) => handleExecutorInput(e, index)}
                      value={executor.name}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} lg={3} xl={3}>
                    <TextInput
                      name={'email'}
                      type={'email'}
                      title={'Executor Email'}
                      label={'email'}
                      handleTextInput={(e: any) => handleExecutorInput(e, index)}
                      value={executor.email}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1} md={1} lg={1} xl={1}>
                    <SwitchButton state={executor.cc} handleChange={() => handleExecutorCc(!executor.cc, index)} title={'Cc on Deal'} />
                  </Grid>
                  <Grid item xs={12} sm={2} md={2} lg={2} xl={2}>
                    <SwitchButton state={executor.ccNotice} handleChange={() => handleExecutorCcNotice(!executor.ccNotice, index)} title={'Cc on Congrats Letter'} />
                  </Grid>
                  <Grid item xs={12} sm={2} md={2} lg={2} xl={2}>
                    <SwitchButton state={executor.ccAll} handleChange={() => handleExecutorCcDocuments(!executor.ccAll, index)} title={'Cc on all Documents'} />
                  </Grid>
                  <Grid item xs={12} sm={1} md={1} lg={1} xl={1} sx={{ cursor: 'pointer', textAlign: 'center' }}>
                    <CloseIcon color="secondary" onClick={() => handleDeleteExecutor(index)} />
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ mb: 4 }}>
            <em>There are currently no executors for this project. Please add one below.</em>
          </Box>
        )}
        <Button sx={{ mt: 1 }} variant="contained" color="primary" onClick={() => addExecutor()}>
          Add an Executor
        </Button>
      </Box>
      <h2>Co-op Commission Structures</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <Box>Please set the co-op commission structures for this project.</Box>
        {coopState.length > 0 ? (
          <div>
            <Box sx={{ my: 2 }}>
              <SwitchButton
                state={commissionIncludeOptions}
                handleChange={() => setCommissionIncludeOptions(!commissionIncludeOptions)}
                title={'Commission includes all Options (Total Price)'}
              />
            </Box>
            {coopState.map((coopStructure: ICoopStructure, index: number) => {
              return (
                <React.Fragment key={index}>
                  <Box>
                    <TextField
                      title={'Coop Structure Name'}
                      name={'name'}
                      fullWidth
                      value={coopStructure.name}
                      label={'Coop Structure Name'}
                      required
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        coopDispatch({
                          type: 'UPDATENAME',
                          payload: {
                            index: index,
                            value: e.target.value,
                          },
                        })
                      }
                    />
                  </Box>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {coopStructure.coopRates.map((coopRate: ICoopRate, numIndex: number) => {
                      return (
                        <Coop
                          key={index}
                          index={numIndex}
                          structureIndex={index}
                          coopRate={coopRate}
                          handleCoopType={handleCoopRateInput}
                          handleCoopInput={handleCoopRateInput}
                          handleDeleteCoop={handleDeleteCoopRate}
                          handleCoopDate={handleCoopDate}
                        />
                      );
                    })}
                  </Grid>
                  <FlexEnd sx={{ mb: 2 }}>
                    <Button sx={{ mt: 1, mr: 1 }} variant="contained" color="primary" onClick={() => addCoopRate(index)}>
                      Add Coop Rate
                    </Button>
                    <Button sx={{ mt: 1 }} variant="contained" color="error" onClick={() => deleteCoopStructure(index)}>
                      Delete Coop Structure
                    </Button>
                  </FlexEnd>
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <Box sx={{ mb: 4 }}>
            <em>There are currently no co-op rates for this project. Please add one below.</em>
          </Box>
        )}
        <Button sx={{ mt: 1 }} variant="contained" color="primary" onClick={() => addCoopStructure()}>
          Add Co-op Structure
        </Button>
      </Box>
      <h2>Mortgage</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <Box sx={{ mb: 4 }}>Please set the mortgage pre-approval threshold</Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextInput
              name={'mortgageLimit'}
              type={'number'}
              title={'Mortgage Pre-approval Limit'}
              label={'mortgageLimit'}
              handleTextInput={(e: any) => setMortgageLimit(parseFloat(e.target.value))}
              value={mortgageLimit.toString()}
              required={true}
              endAdornment={'%'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextInput
              name={'mortgageMinDay'}
              type={'number'}
              title={'Mortgage Minimum Days'}
              label={'mortgage Min Day'}
              handleTextInput={(e: any) => setMortgageMinDay(parseFloat(e.target.value))}
              value={mortgageMinDay.toString()}
              required={true}
            />
          </Grid>
        </Grid>
      </Box>
      <h2>Occupancy</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <Box sx={{ mb: 2 }}>Please set the Occupancy Dates</Box>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item sm={3} xs={12}>
              <DatePicker
                views={['year', 'month', 'day']}
                label={'First Tentative Occupancy Date (YYYY/MM/DD)'}
                value={firstTentativeOccupancy ? new Date(firstTentativeOccupancy) : null}
                onChange={(newValue) => setFirstTentativeOccupancy(newValue)}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <DatePicker
                views={['year', 'month', 'day']}
                label={'Final Tentative Occupancy Date'}
                value={finalTentativeOccupancy ? new Date(finalTentativeOccupancy) : null}
                onChange={(newValue) => setFinalTentativeOccupancy(newValue)}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <DatePicker
                views={['year', 'month', 'day']}
                label={'Firm Occupancy Date'}
                value={firmOccupancy ? new Date(firmOccupancy) : null}
                onChange={(newValue) => setFirmOccupancy(newValue)}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <DatePicker
                views={['year', 'month', 'day']}
                label={'Outside Occupancy Date'}
                value={outsideOccupancy ? new Date(outsideOccupancy) : null}
                onChange={(newValue) => setOutsideOccupancy(newValue)}
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      <h2>Miscellaneous</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <SwitchButton
              state={hideOccupancy}
              handleChange={() => setHideOccupancy(!hideOccupancy)}
              title={'Hide Occupancy on Sent Deal Email'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label={'Default Zero Value Option Merge Field'}
              title={'Default Zero Value Option Merge Field'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDefaultZeroValue(e.target.value)}
              value={defaultZeroValue}
              required={true}
            />
          </Grid>
        </Grid>
        <Box>
          <Typography sx={{ mb: 2 }} variant="h2" gutterBottom>
            <strong>Tags</strong>
          </Typography>
          <Box>
            <TextField
              fullWidth
              label="Enter a Tag"
              variant="outlined"
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              size="medium"
            />
            <Button sx={{ mt: 2 }} variant="contained" color="primary" onClick={() => addTag()}>
              Add Tag
            </Button>
          </Box>
          {tags.length ? (
            <Grid container spacing={2}>
              {tags.map((tag: string, index: number) => {
                return (
                  <Grid key={index} item sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', p: 1, border: '1px solid #000', borderRadius: '8px' }}>
                      {tag}
                      <CloseIcon sx={{ ml: 1, cursor: 'pointer' }} onClick={() => deleteTag(index)} />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : null}
        </Box>
      </Box>
      <h2>Leasing</h2>
      <Box
        sx={{
          border: '1px solid #000',
          padding: '30px 20px',
          position: 'relative',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <SwitchButton state={leasing} handleChange={() => setLeasing(!leasing)} title={'Enable Leasing'} />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              id="lease-users"
              options={users}
              freeSolo={false}
              getOptionLabel={(option: any) => option.fullName}
              isOptionEqualToValue={(option, value) => option === value}
              value={leaseCc}
              onChange={(e, value) => setLeaseCc(value)}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Users to be cc'd on leasing envelopes" />}
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ my: 2, py: 2 }}>
        <Button form="project" type="submit" variant="contained" color="success">
          Save Project
        </Button>
      </Box>
    </form>
  );
};

interface ChildProps {
  title: string;
  type: string;
}

const GETUSERS = gql`
  query userMany($filter: FilterFindManyUserInput!) {
    userMany(filter: $filter) {
      _id
      email
      fullName
    }
  }
`;

const CREATEPROJECT = gql`
  mutation projectCreateOne($record: CreateOneProjectInput!) {
    projectCreateOne(record: $record) {
      _id
      name
      developerName
      addresses {
        streetAddress
        city
        province
        postalCode
        country
      }
      hoursOfOperation {
        day
        open
        close
      }
      email
      docusignEmail
      trackingPhone
      salesTrackingPhone
      mergeTemplates {
        name
        mergeTemplate {
          _id
        }
        apsTemplates {
          name
          pageNumber
        }
      }
      options {
        name
        totalAvailable
        price
        type
        allowedUnits
        rent
      }
      depositStructures {
        name
        deposits {
          name
          type
          amount
          daysDue
          dateType
          dueDate
        }
        default
      }
      status {
        name
        code
        color
        type
        category
      }
      tags
    }
  }
`;

const UPDATEUSER = gql`
  mutation userUpdateById($_id: MongoID!, $record: UpdateByIdUserInput!) {
    userUpdateById(_id: $_id, record: $record) {
      record {
        _id
        fullName
        projectAccess {
          project {
            _id
          }
          access
        }
      }
    }
  }
`;

const UPDATEPROJECT = gql`
  mutation projectUpdateById($_id: MongoID!, $record: UpdateByIdProjectInput!) {
    projectUpdateById(_id: $_id, record: $record) {
      record {
        _id
        name
        developerName
        email
        docusignEmail
        trackingPhone
        salesTrackingPhone
        addresses {
          streetAddress
          city
          province
          postalCode
          country
        }
        hoursOfOperation {
          day
          open
          close
        }
        options {
          name
          totalAvailable
          price
          allowedUnits
          rent
          type
        }
        depositStructures {
          name
          deposits {
            name
            type
            amount
            daysDue
            dateType
            dueDate
          }
          default
        }
        mergeTemplates {
          name
          mergeTemplate {
            name
            _id
          }
          apsTemplates {
            name
            apsTemplate {
              _id
              name
              type
            }
            pageNumber
            attachToAps
          }
        }
        status {
          name
          code
          color
          type
          category
        }
        executors {
          name
          email
          cc
          ccAll
          ccNotice
        }
        mortgageLimit
        mortgageMinDay
        hideOccupancy
        defaultZeroValue
        lawyer
        tagLine
        tags
        salesOffice
        adjustments {
          name
          type
        }
        commissionIncludeOptions
        emailTemplates {
          _id
          name
          subject
          html
        }
        logoGetUrl
        logoPutUrl
        imageGetUrl
        imagePutUrl
        developerLogoGetUrl
        developerLogoPutUrl
        partnerLogoGetUrl
        partnerLogoPutUrl
        coopStructures {
          name
          coopRates {
            type
            days
            date
            description
            percentage
            fixedAmount
          }
        }
        firstTentativeOccupancy
        finalTentativeOccupancy
        firmOccupancy
        outsideOccupancy
        active
        acknowledgement
        executionDeposit
        automatedReminders
        postRegistration
        netHst
        decorModels {
          modelType
          allowed
        }
        portal {
          primaryColor
        }
        combinedProjects {
          _id
          name
        }
        leasing
        leaseCc {
          _id
          email
          fullName
        }
      }
    }
  }
`;

export default GeneralInfo;
